import type { FC } from 'react';
import * as React from 'react';
import { useCallback, useState, useEffect, useRef } from 'react';
import { Modal } from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import {
  useSetupMangopayDirectDebitValidationSchema,
  processMangopayValidationErrors,
  filterErrorsByTouched,
} from '@pflegenavi/shared/frontend';
import * as Yup from 'yup';
import type { FormikErrors, FormikTouched } from 'formik';
import { useFormik } from 'formik';
import {
  useGetMangoPayDirectDebitResident,
  useValidateFamilyMemberRecipientData,
  type Country,
  type SetupMangoPayDirectDebitResponse,
  type ValidationErrorResponse,
} from '@pflegenavi/frontend/api-nursing-home';
import { ClientError } from '@pflegenavi/shared-frontend/platform';
import { useSnackbar } from 'notistack';
import { useFormatting } from '@pflegenavi/frontend/localization';
import { useNavigate } from 'react-router-dom';
import { fmAppMainPages } from '@pflegenavi/frontend/routing';
import { useInitializeFM } from '../../../hooks/useInitializeFM';
import { PaymentMethodSelectionModalView } from './PaymentMethodSelectionModalView';

interface PaymentMethodSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onUseExistingMandate: (amount: number) => Promise<void>;
  onUseNewBankAccount: (
    amount: number,
    bankAccount: {
      iban: string;
      first_name: string;
      last_name: string;
      owner_address: {
        city: string;
        address_line_1: string;
        address_line_2?: string;
        country: string;
        postal_code: string;
      };
    }
  ) => Promise<SetupMangoPayDirectDebitResponse>;
  existingMandateActive: boolean;
  countries: Country[];
  amount: number;
  isLoading?: boolean;
}

type PaymentMethod = 'existing' | 'new';

interface BankAccountFormValues {
  firstName: string;
  lastName: string;
  iban: string;
  city: string;
  addressLine1: string;
  addressLine2?: string;
  country: string;
  postalCode: string;
}

export const PaymentMethodSelectionModal: FC<
  PaymentMethodSelectionModalProps
> = ({
  open,
  onClose,
  onUseExistingMandate,
  onUseNewBankAccount,
  existingMandateActive,
  countries,
  amount,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { selectedResident } = useInitializeFM();

  // Refs for async validation state management
  const touchedRef = useRef<FormikTouched<BankAccountFormValues>>({});
  const unfilteredErrorsRef = useRef<FormikErrors<BankAccountFormValues>>({});

  // Validation mutation for MangoPay recipient validation
  const { mutateAsync: validateRecipientData } =
    useValidateFamilyMemberRecipientData();

  const { data: residentMandate } = useGetMangoPayDirectDebitResident({
    paymentProcessorMode:
      selectedResident?.tenantPaymentProcessorMode ?? 'live',
    tenant: selectedResident?.tenant ?? '',
    resident_id: selectedResident?.residentId ?? '',
  });

  // If there's no active mandate, we'll always use new bank account
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
    existingMandateActive ? 'existing' : 'new'
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize state for the modal steps
  const [currentStep, setCurrentStep] = useState<'select' | 'confirm' | 'new'>(
    existingMandateActive ? 'select' : 'new'
  );

  // Reset the current step whenever the mandate status changes
  useEffect(() => {
    setCurrentStep(existingMandateActive ? 'select' : 'new');
    setPaymentMethod(existingMandateActive ? 'existing' : 'new');
  }, [existingMandateActive]);

  const [pendingBankAccount, setPendingBankAccount] = useState<{
    iban?: string;
    isNewAccount: boolean;
  }>({ isNewAccount: !existingMandateActive });

  const validationSchema = Yup.object(
    useSetupMangopayDirectDebitValidationSchema()
  );

  // Async validation function that calls MangoPay validation API
  const validateAsync = useCallback(
    async (
      values: BankAccountFormValues
    ): Promise<FormikErrors<BankAccountFormValues>> => {
      // Skip validation if we don't have the required context
      if (
        !selectedResident?.tenant ||
        !selectedResident?.residentId ||
        !selectedResident?.tenantPaymentProcessorMode
      ) {
        return {};
      }

      // Skip validation if no meaningful data to validate
      const hasIban = !!values.iban?.trim();
      const hasAddressData =
        !!values.city?.trim() ||
        !!values.addressLine1?.trim() ||
        !!values.postalCode?.trim();

      if (!hasIban && !hasAddressData) {
        return {};
      }

      // Run sync validation first - sync errors take precedence over async API errors
      // This ensures specific errors like "Address cannot contain '/'" are shown
      // instead of generic API errors like "Invalid format"
      try {
        await validationSchema.validate(values, { abortEarly: false });
      } catch (yupError) {
        if (yupError instanceof Yup.ValidationError) {
          const syncErrors: FormikErrors<BankAccountFormValues> = {};
          yupError.inner.forEach((err) => {
            if (err.path) {
              syncErrors[err.path as keyof BankAccountFormValues] = err.message;
            }
          });
          // Return sync errors - don't call API if sync validation fails
          return syncErrors;
        }
      }

      try {
        // Transform form values to MangoPay recipient format
        const recipientData: Record<string, unknown> = {};

        if (values.firstName?.trim()) {
          recipientData.FirstName = values.firstName;
        }
        if (values.lastName?.trim()) {
          recipientData.LastName = values.lastName;
        }
        if (values.iban?.trim()) {
          recipientData.IBAN = values.iban;
        }

        // Build address only if we have any address fields
        const address: Record<string, string> = {};
        if (values.addressLine1?.trim()) {
          address.AddressLine1 = values.addressLine1;
        }
        if (values.addressLine2?.trim()) {
          address.AddressLine2 = values.addressLine2;
        }
        if (values.city?.trim()) {
          address.City = values.city;
        }
        if (values.postalCode?.trim()) {
          address.PostalCode = values.postalCode;
        }
        if (values.country?.trim()) {
          address.Country = values.country;
        }

        if (Object.keys(address).length > 0) {
          recipientData.Address = address;
        }

        await validateRecipientData({
          params: {
            paymentProcessorMode: selectedResident.tenantPaymentProcessorMode,
            tenant: selectedResident.tenant,
            resident_id: selectedResident.residentId,
          },
          body: {
            currency: 'EUR',
            country: values.country,
            payout_method_type: 'LocalBankTransfer',
            recipient_type: 'INDIVIDUAL',
            recipient_data: recipientData,
          },
        });

        // Validation passed - clear errors
        unfilteredErrorsRef.current = {};
        return {};
      } catch (error: unknown) {
        // Handle validation errors from the API
        if (error instanceof ClientError) {
          const validationError = error as ValidationErrorResponse &
            ClientError;

          if (validationError.errors && Array.isArray(validationError.errors)) {
            // Translate error messages
            const translatedErrors = validationError.errors.map((err) => {
              const translationKey = `mangopay.recipient.errors.${err.message}`;
              const translatedMessage = t(translationKey);

              return {
                field: err.field,
                message:
                  translatedMessage === translationKey
                    ? err.message
                    : translatedMessage,
              };
            });

            // Transform to Formik format and store unfiltered errors
            const nestedErrors =
              processMangopayValidationErrors(translatedErrors);
            unfilteredErrorsRef.current =
              nestedErrors as FormikErrors<BankAccountFormValues>;

            // Filter errors to only show for touched fields
            const filteredErrors = filterErrorsByTouched(
              nestedErrors,
              touchedRef.current
            );

            return filteredErrors as FormikErrors<BankAccountFormValues>;
          }
        }

        // Return empty errors for unexpected error types
        return {};
      }
    },
    [selectedResident, validateRecipientData, t, validationSchema]
  );

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      iban: '',
      city: '',
      addressLine1: '',
      addressLine2: '',
      country: '',
      postalCode: '',
    } as BankAccountFormValues,
    enableReinitialize: true,
    onSubmit: async (values: BankAccountFormValues) => {
      if (paymentMethod === 'existing') {
        return;
      }

      setIsSubmitting(true);
      try {
        await onUseNewBankAccount(amount, {
          iban: values.iban,
          first_name: values.firstName,
          last_name: values.lastName,
          owner_address: {
            city: values.city,
            address_line_1: values.addressLine1,
            // Only include address_line_2 if it has a value (MangoPay rejects empty strings)
            ...(values.addressLine2
              ? { address_line_2: values.addressLine2 }
              : {}),
            country: values.country,
            postal_code: values.postalCode,
          },
        });
        onClose();
      } finally {
        setIsSubmitting(false);
      }
    },
    validationSchema,
    validate: validateAsync,
    validateOnChange: false, // Only validate on blur to avoid excessive API calls
    validateOnBlur: true,
    validateOnMount: false,
  });

  // Keep touched ref in sync with formik touched state
  // This is needed for filtering validation errors
  if (formik.touched !== touchedRef.current) {
    touchedRef.current = formik.touched;
  }

  const countryOptions = React.useMemo(() => {
    return countries.map((country) => ({
      label: t(`countries.${country.iso_code}`),
      value: country.iso_code,
    }));
  }, [countries, t]);

  const onChangeCountry = useCallback(
    (newCountryId: string) => {
      if (formik.values.country === newCountryId) {
        return;
      }

      formik.setValues((values) => ({
        ...values,
        country: newCountryId,
      }));
      formik.setFieldError('country', undefined);
    },
    [formik]
  );

  const onCountryBlur = useCallback(() => {
    formik.setFieldTouched('country', true);
  }, [formik]);

  // Handler to touch all fields and validate before proceeding
  // This ensures validation errors are shown for all fields, not just touched ones
  const onInitiateSubmit = useCallback(async () => {
    const allTouched = {
      firstName: true,
      lastName: true,
      iban: true,
      city: true,
      addressLine1: true,
      addressLine2: true,
      country: true,
      postalCode: true,
    };

    // Update the ref immediately so validation sees all fields as touched
    touchedRef.current = allTouched;

    // Update formik's touched state
    await formik.setTouched(allTouched);

    // Validate the form
    const errors = await formik.validateForm();

    // Return true if form is valid (no errors)
    return Object.keys(errors).length === 0;
  }, [formik]);

  const handleNextStep = async () => {
    // This is only called from the select step with existing mandate
    if (paymentMethod === 'existing') {
      // Prepare for confirmation step with existing mandate details
      setPendingBankAccount({
        isNewAccount: false,
        iban: residentMandate?.data?.bank_account?.iban,
      });
      setCurrentStep('confirm');
    } else {
      // For new bank account, touch all fields and validate
      const isValid = await onInitiateSubmit();
      if (isValid) {
        // Prepare for confirmation step with new bank account details
        setPendingBankAccount({
          iban: formik.values.iban,
          isNewAccount: true,
        });
        setCurrentStep('confirm');
      }
    }
  };

  const handleBack = () => {
    // If we're coming from the 'new' step (no existing mandate)
    if (!existingMandateActive) {
      setCurrentStep('new');
    } else {
      // If we have an existing mandate, go back to selection
      setCurrentStep('select');
    }
  };

  const handleFinalPayment = async () => {
    setIsSubmitting(true);
    try {
      if (pendingBankAccount.isNewAccount) {
        // Submit the form with bank account details
        const response = await onUseNewBankAccount(amount, {
          iban: formik.values.iban,
          first_name: formik.values.firstName,
          last_name: formik.values.lastName,
          owner_address: {
            city: formik.values.city,
            address_line_1: formik.values.addressLine1,
            // Only include address_line_2 if it has a value (MangoPay rejects empty strings)
            ...(formik.values.addressLine2
              ? { address_line_2: formik.values.addressLine2 }
              : {}),
            country: formik.values.country,
            postal_code: formik.values.postalCode,
          },
        });

        // Extract the redirect URL from the response
        if (response?.data?.mandate?.document_url) {
          // Redirect immediately to Mangopay without delay
          window.location.href = response.data.mandate.document_url;

          // Return early to prevent dashboard navigation
          return;
        }
      } else {
        // Use existing mandate
        await onUseExistingMandate(amount);
      }

      // Only reached if we're using existing mandate or no redirect URL was provided
      navigate(fmAppMainPages.DASHBOARD);
      onClose();
    } catch (error: unknown) {
      // Check if this is a MangoPay validation error with field-specific errors
      const validationError = error as ValidationErrorResponse | undefined;
      if (validationError?.errors && Array.isArray(validationError.errors)) {
        // Transform MangoPay errors to Formik errors and display them
        const formikErrors = processMangopayValidationErrors(
          validationError.errors.map((err) => {
            const translationKey = `mangopay.recipient.errors.${err.message}`;
            const translatedMessage = t(translationKey);
            return {
              field: err.field,
              message:
                translatedMessage === translationKey
                  ? err.message
                  : translatedMessage,
            };
          })
        );
        formik.setErrors(formikErrors);

        // Show first error in snackbar
        const firstError = validationError.errors[0];
        if (firstError) {
          const translationKey = `mangopay.recipient.errors.${firstError.message}`;
          const translatedMessage = t(translationKey);
          enqueueSnackbar(
            translatedMessage === translationKey
              ? firstError.message
              : translatedMessage,
            { variant: 'error' }
          );
        }

        // Go back to bank details entry step
        setCurrentStep('new');
      } else {
        enqueueSnackbar(t('errors.something-went-wrong'), { variant: 'error' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Mask IBAN for display
  const maskIban = (iban?: string) => {
    if (!iban) {
      return '';
    }
    // Only show the last 4 characters of the IBAN
    return iban.length > 4 ? `****${iban.slice(iban.length - 4)}` : iban;
  };

  return (
    <Modal open={open} handleClose={onClose}>
      <PaymentMethodSelectionModalView
        open={open}
        onClose={onClose}
        amount={amount}
        existingMandateActive={existingMandateActive}
        isLoading={isLoading}
        isSubmitting={isSubmitting}
        currentStep={currentStep}
        paymentMethod={paymentMethod}
        onPaymentMethodChange={setPaymentMethod}
        onNextStep={handleNextStep}
        onBack={handleBack}
        onFinalPayment={handleFinalPayment}
        formik={formik}
        countryOptions={countryOptions}
        onChangeCountry={onChangeCountry}
        onCountryBlur={onCountryBlur}
        pendingBankAccount={pendingBankAccount}
        maskIban={maskIban}
        fCurrencyCents={fCurrencyCents}
      />
    </Modal>
  );
};
